/**
 * Трекер посещений (собственная аналитика, без GA/Yandex).
 * Считает и анонимов, и авторизованных. Не должен ломать UX — все ошибки глушим.
 *
 *  visitorId — постоянный (localStorage) → человек/устройство между визитами;
 *  sessionId — один визит (скользящее протухание 30 мин бездействия);
 *  отправка — fetch(keepalive) с токеном, если залогинен; sendBeacon на выгрузке.
 */
import { getWellandStoredToken } from '../../auth/storageKeys';

const VID_KEY = 'welland_vid';
const SID_KEY = 'welland_sid';
const SID_EXP_KEY = 'welland_sid_exp';
const INTERNAL_KEY = 'welland_internal';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // новая сессия после 30 мин бездействия

function uuid() {
    try {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    } catch { /* ignore */ }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

function lsGet(k) { try { return localStorage.getItem(k); } catch { return null; } }
function lsSet(k, v) { try { localStorage.setItem(k, v); } catch { /* ignore */ } }

/**
 * Служебный (владелец/админ) браузер — не считаем в аналитике. Помечается
 * один раз при успешном входе в /admin (см. markInternalVisitor) и держится
 * в localStorage навсегда: одна и та же вкладка/браузер больше не шлёт
 * трекинг ни на лендинге, ни в приложении, ни в кабинете.
 */
function isInternalVisitor() {
    return lsGet(INTERNAL_KEY) === '1';
}

/** Пометить этот браузер как служебный (вызывается после успешного входа в /admin). */
export function markInternalVisitor() {
    lsSet(INTERNAL_KEY, '1');
}

/** Постоянный идентификатор посетителя. */
function getVisitorId() {
    let id = lsGet(VID_KEY);
    if (!id) { id = uuid(); lsSet(VID_KEY, id); }
    return id;
}

/** Читает текущий sessionId, если он не протух; иначе null (без создания). */
function readValidSessionId() {
    const sid = lsGet(SID_KEY);
    const exp = Number(lsGet(SID_EXP_KEY) || 0);
    if (sid && exp && Date.now() < exp) return sid;
    return null;
}

/** Возвращает { sessionId, isNew }; продлевает окно активности. Создаёт сессию при надобности. */
function getOrCreateSession() {
    let sid = readValidSessionId();
    const isNew = !sid;
    if (!sid) { sid = uuid(); lsSet(SID_KEY, sid); }
    lsSet(SID_EXP_KEY, String(Date.now() + SESSION_TIMEOUT_MS));
    return { sessionId: sid, isNew };
}

/** Продлевает существующую сессию (для heartbeat); null если сессии нет. */
function touchSession() {
    const sid = readValidSessionId();
    if (!sid) return null;
    lsSet(SID_EXP_KEY, String(Date.now() + SESSION_TIMEOUT_MS));
    return sid;
}

/** Раздел сайта по маршруту: landing | auth | dashboard | app | other. */
export function sectionFromPath(pathname = '') {
    if (pathname.startsWith('/app')) return 'app';
    if (pathname.startsWith('/dashboard')) return 'dashboard';
    if (pathname.startsWith('/auth') || pathname.startsWith('/login') || pathname.startsWith('/register') || pathname.startsWith('/reset-password')) return 'auth';
    if (pathname === '/') return 'landing';
    return 'other'; // служебные (privacy/terms) — в графике разделов не показываем
}

function getUtm() {
    try {
        const p = new URLSearchParams(window.location.search);
        const out = {};
        const s = p.get('utm_source'); if (s) out.source = s;
        const m = p.get('utm_medium'); if (m) out.medium = m;
        const c = p.get('utm_campaign'); if (c) out.campaign = c;
        return out;
    } catch { return {}; }
}

/**
 * Отправка события. По умолчанию fetch(keepalive) с токеном (если залогинен) —
 * так залогиненная навигация сразу привязывается к user. На выгрузке — sendBeacon.
 */
function post(action, data, { beacon = false } = {}) {
    try {
        const url = '/api/track/' + action;
        const body = JSON.stringify(data);
        if (beacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
            navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
            return;
        }
        const headers = { 'Content-Type': 'application/json' };
        const token = getWellandStoredToken();
        if (token) headers.Authorization = `Bearer ${token}`;
        fetch(url, { method: 'POST', headers, body, keepalive: true, credentials: 'same-origin' }).catch(() => {});
    } catch { /* трекинг не должен ломать UX */ }
}

// Антидубль: один и тот же путь в пределах окна не считаем повторно
// (React StrictMode в dev и быстрые ре-рендеры дублируют эффект).
let _lastPath = null;
let _lastPathAt = 0;
const PAGEVIEW_DEDUPE_MS = 1500;

/** Просмотр страницы (смена маршрута). Первый в сессии дополнительно шлёт start. */
export function trackPageview(pathname) {
    try {
        if (isInternalVisitor()) return;
        const now = Date.now();
        if (pathname === _lastPath && now - _lastPathAt < PAGEVIEW_DEDUPE_MS) return;
        _lastPath = pathname;
        _lastPathAt = now;

        const visitorId = getVisitorId();
        const { sessionId, isNew } = getOrCreateSession();
        const section = sectionFromPath(pathname);
        if (isNew) {
            post('start', {
                visitorId,
                sessionId,
                section,
                path: pathname,
                referrer: (typeof document !== 'undefined' ? document.referrer : '') || '',
                utm: getUtm(),
            });
        }
        post('pageview', { visitorId, sessionId, section, path: pathname });
    } catch { /* ignore */ }
}

/** Heartbeat: продлевает время визита. Не создаёт сессию, если её нет. */
export function trackPing({ beacon = false } = {}) {
    try {
        if (isInternalVisitor()) return;
        const sessionId = touchSession();
        if (!sessionId) return;
        post('ping', { visitorId: getVisitorId(), sessionId }, { beacon });
    } catch { /* ignore */ }
}

/** Привязать текущий визит/посетителя к пользователю (после логина). */
export function linkAuthToAnalytics() {
    try {
        if (isInternalVisitor() || !getWellandStoredToken()) return;
        const sessionId = readValidSessionId();
        post('identify', { visitorId: getVisitorId(), sessionId: sessionId || undefined });
    } catch { /* ignore */ }
}
